<template>
  <ModalBase
    :open="isOpen"
    :title="previewFile?.name || ''"
    content-padding="0 24px"
    footer-padding="0"
    @close="closeModal"
  >
    <template #controls>
      <div class="controls">
        <ActionButton
          v-for="action in actions"
          :key="action.actionName"
          class="controls__button"
          :size="'extra-small-icon'"
          :action="action"
          @action="(action) => $emit('action', action.actionName, [previewFile!])"
        />
      </div>
    </template>
    <template #content>
      <div class="content">
        <FilePreview
          :new-page="pdfCurrentPage"
          :preview-file="previewFile"
          :preview-type="previewType"
          :file-blob-source="blobUrl"
          :text-content="textContent"
          :loading-file="isLoading"
          :file-error-text="errorMessage"
          @pdf-page="setCurrentPdfPage"
          @pdf-numpages="setNumberOfPdfPages"
          @pdf-error="(error) => (pdfError = error)"
          @preview-file="isOpen = true"
        ></FilePreview>
        <div
          v-if="detailsOpen || previewType === 'none' || (previewType === 'pdf' && pdfError)"
          class="divider"
        ></div>
        <div
          v-if="detailsOpen || previewType === 'none' || (previewType === 'pdf' && pdfError)"
          class="file-info-container"
        >
          <FileMetaInfo :directoryItem="previewFile" />
        </div>
      </div>
    </template>
    <template #footer>
      <div
        class="footer-buttons"
        :style="{
          flexDirection: previewType === 'pdf' && !pdfError ? 'row' : 'row-reverse'
        }"
      >
        <div
          v-if="previewType === 'pdf' && pdfCurrentPage && !pdfError"
          class="footer-buttons__navigation"
        >
          <button
            class="footer-buttons__navigation__button"
            :disabled="pdfCurrentPage <= 1"
            @click="changePage('prev')"
          >
            <IconChevronLeft />
          </button>
          <span class="footer-buttons__navigation__text"
            >{{ pdfCurrentPage }} / {{ pdfNumPages }}</span
          >
          <button
            class="footer-buttons__navigation__button"
            :disabled="pdfCurrentPage >= pdfNumPages"
            @click="changePage('next')"
          >
            <IconChevronRight />
          </button>
        </div>
        <ButtonText
          size="small"
          :button-text="t('general__text__details')"
          class="footer-buttons__button"
          :class="{
            disabled:
              previewType === 'none' || (previewType === 'pdf' && pdfError) || filePreviewFailed
          }"
          :disabled="
            previewType === 'none' ||
            (previewType === 'pdf' && pdfError !== null) ||
            filePreviewFailed
          "
          @onClicked="detailsOpen = !detailsOpen"
        >
          <template #left><IconLayoutRightSmall /></template>
        </ButtonText>
      </div>
    </template>
  </ModalBase>
</template>
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Action, DirectoryItem } from './types'
import IconChevronRight from '@/design-system/assets/icons/IconChevronRight.vue'
import IconChevronLeft from '@/design-system/assets/icons/IconChevronLeft.vue'
import ActionButton from './ActionButton.vue'
import ModalBase from './ModalBase.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import IconLayoutRightSmall from '@/design-system/assets/icons/IconLayoutRightSmall.vue'
import FileMetaInfo from './FileMetaInfo.vue'
import FilePreview from './FilePreview.vue'
import { useFilePreview } from './useFilePreview'

type Props = {
  open?: boolean
  showDetails?: boolean
  previewFile?: DirectoryItem<'file'>
}
const props = withDefaults(defineProps<Props>(), {
  open: false,
  showDetails: false,
  previewFile: undefined
})

const { showDetails, previewFile } = toRefs(props)
const { previewType, blobUrl, textContent, isLoading, errorMessage } = useFilePreview(previewFile)
const isOpen = ref(props.open)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'action', action: string, items: DirectoryItem[]): void
}>()

const { t } = useI18n()

const pdfNumPages = ref(1)
const pdfCurrentPage = ref(1)
const pdfError = ref<string | null>(null)
const detailsOpen = ref(showDetails.value)
const filePreviewFailed = ref(false)
const templateColumns = computed(() => {
  if (
    detailsOpen.value ||
    previewType.value === 'none' ||
    (previewType.value === 'pdf' && pdfError.value)
  ) {
    return '1fr auto 360px'
  }
  return '1fr'
})

const actions: Action[] = [
  { actionName: 'download', labelKey: 'general__text__download', icon: 'IconDownload' },
  { actionName: 'move', labelKey: 'general__text__move', icon: 'IconMoveFolder' },
  { actionName: 'archive', labelKey: 'general__text__archive', icon: 'IconArchive' },
  { actionName: 'delete', labelKey: 'general__text__delete', icon: 'IconTrash2Small' }
]

function setCurrentPdfPage(page: number) {
  pdfCurrentPage.value = page
}
function setNumberOfPdfPages(numPages: number) {
  pdfNumPages.value = numPages
}

function changePage(change: string) {
  if (change === 'next') {
    if (pdfCurrentPage.value < pdfNumPages.value) pdfCurrentPage.value += 1
  } else if (change === 'prev') {
    if (pdfCurrentPage.value > 1) pdfCurrentPage.value -= 1
  }
}

const closeModal = () => {
  isOpen.value = false
  emit('close')
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeModal()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
    if (!newValue) detailsOpen.value = false
  }
)
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.controls {
  display: flex;
  gap: 8px;
  align-items: center;
  color: variables.$color-primary;
  padding-right: 8px;
  background-color: variables.$color-pure-white;
  border-right: 1px solid variables.$color-greyscale-line-grey;

  .controls__button {
    border-radius: variables.$border-radius-small;
    &:hover {
      background-color: variables.$color-primary;
      color: #fff;
      :deep(.button-base .text) {
        color: #fff;
      }
    }
  }
}

.content {
  display: grid;
  grid-template-columns: v-bind(templateColumns);
  max-height: 65vh;
  .divider {
    display: flex;
    width: 1px;
    margin-right: 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    max-height: 100%;
    background: variables.$color-greyscale-line-grey;
  }
  .file-info-container {
    display: flex;
    width: 360px;
    flex-direction: column;
    align-items: flex-start;
    margin: 16px 0;
  }
}
.footer-buttons {
  background-color: variables.$color-greyscale-background;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  padding: 8px 24px;
  &__button {
    color: variables.$color-greyscale-content;
    &:hover {
      background-color: variables.$color-primary;
      color: #fff;
      :deep(.button-base .text) {
        color: #fff;
      }
    }
    &.disabled {
      background-color: variables.$color-greyscale-background;
      color: variables.$color-greyscale-secondary;
      cursor: not-allowed;
      &:hover {
        color: variables.$color-greyscale-secondary;
        :deep(.button-base .text) {
          color: variables.$color-greyscale-secondary;
        }
      }
    }
  }
  &__navigation {
    align-items: center;
    color: variables.$color-greyscale-content;
    display: flex;
    gap: 10px;
    @include font-mixins.desktop-bold-label;
    &__button {
      all: unset;
      font-size: 24px;
      height: 24px;
      text-align: center;
      width: 24px;
      cursor: pointer;
      &:focus-visible {
        outline: auto;
      }
    }
  }
}
</style>
